import { styled, GetProps, YStack, Text } from 'tamagui'
import type { ReactNode } from 'react'

const SelectionCardFrame = styled(YStack, {
  name: 'SelectionCard',
  tag: 'button',
  
  // Base styles - Brutalist selection card
  backgroundColor: '$backgroundMuted',
  borderWidth: 2,
  borderColor: '$borderColor',
  borderRadius: 0,
  padding: '$4',
  cursor: 'pointer',
  alignItems: 'center',
  justifyContent: 'center',
  
  pressStyle: {
    opacity: 0.9,
    scale: 0.98,
  },
  
  hoverStyle: {
    borderColor: '$colorMuted',
  },
  
  variants: {
    state: {
      selected: {
        borderColor: '$accent',
        backgroundColor: '$background',
      },
      default: {},
    },
    size: {
      sm: {
        padding: '$2',
        minHeight: 60,
      },
      md: {
        padding: '$4',
        minHeight: 80,
      },
      lg: {
        padding: '$6',
        minHeight: 100,
      },
    },
  } as const,
  
  defaultVariants: {
    size: 'md',
  },
})

const SelectionCardLabel = styled(Text, {
  name: 'SelectionCardLabel',
  fontSize: 10,
  fontWeight: '600',
  textTransform: 'uppercase',
  letterSpacing: 2,
  color: '$colorMuted',
  marginBottom: '$2',
})

export interface SelectionCardProps extends Omit<GetProps<typeof SelectionCardFrame>, 'state'> {
  label?: string
  children: ReactNode
  selected?: boolean
  onClick?: () => void
}

export function SelectionCard({ label, children, selected, onClick, ...props }: SelectionCardProps) {
  return (
    <SelectionCardFrame
      role="button"
      aria-pressed={selected}
      state={selected ? 'selected' : 'default'}
      onPress={onClick}
      {...props}
    >
      {label && <SelectionCardLabel>{label}</SelectionCardLabel>}
      {children}
    </SelectionCardFrame>
  )
}

SelectionCard.displayName = 'SelectionCard'
